import React, { useState } from 'react';
import {
  Settings, FolderKanban, Tag, Layers,
  TrendingUp, ChevronLeft, ChevronRight, Building2,
} from 'lucide-react';
import ProjectItemConfig from '../tabs/buildingTypeTab';
import WorkItemConfig from '../tabs/workCategoryTab';
import GeneralWorkCategoryConfig from '../tabs/generalWorkCategoryTab';
import CACTab from '../tabs/cacTab';
import CompanyProfileTab from '../tabs/companyProfileTab';
import { useAuth } from '../context/AuthContext';

type Section = 'projectItem' | 'workItem' | 'generalWorkItem' | 'cacItem' | 'companyProfile';

interface SectionDef {
  id: Section;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  iconColor: string;
  iconBg: string;
  badgeColor: string;
}

const BASE_SECTIONS: SectionDef[] = [
  {
    id: 'workItem',
    title: 'Rubros de Obra',
    subtitle: 'Categorías de trabajo específicas',
    description:
      'Administrá las categorías de trabajo que se aplican a obras de construcción. Cada rubro puede asignarse a uno o varios tipos de obra con su porcentaje de distribución.',
    icon: <FolderKanban className="w-6 h-6" />,
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    badgeColor: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300',
  },
  {
    id: 'generalWorkItem',
    title: 'Rubros Generales',
    subtitle: 'Categorías de uso transversal',
    description:
      'Gestioná los rubros generales disponibles en el sistema. Son independientes de los tipos de obra y se utilizan en presupuestos y reportes de carácter general.',
    icon: <Tag className="w-6 h-6" />,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/30',
    badgeColor: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300',
  },
  {
    id: 'projectItem',
    title: 'Tipos de Obras',
    subtitle: 'Plantillas de proyectos de construcción',
    description:
      'Definí los tipos de obras disponibles en el sistema, asignando los rubros y sus distribuciones porcentuales. Los porcentajes deben sumar exactamente 100% para que el tipo sea válido.',
    icon: <Layers className="w-6 h-6" />,
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    iconBg: 'bg-indigo-100 dark:bg-indigo-900/30',
    badgeColor: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-300',
  },
  {
    id: 'cacItem',
    title: 'Índices CAC',
    subtitle: 'Costo Argentino de la Construcción',
    description:
      'Visualizá y actualizá los índices CAC importados desde fuentes oficiales. Se utilizan para calcular ajustes de precios en contratos y certificaciones de obra.',
    icon: <TrendingUp className="w-6 h-6" />,
    iconColor: 'text-orange-600 dark:text-orange-400',
    iconBg: 'bg-orange-100 dark:bg-orange-900/30',
    badgeColor: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300',
  },
];

const OWNER_SECTION: SectionDef = {
  id: 'companyProfile',
  title: 'Mi Empresa',
  subtitle: 'Perfil y datos de la empresa',
  description:
    'Visualizá y editá la información que registraste al crear tu empresa: razón social, CUIT, rubro, datos de contacto y ubicación.',
  icon: <Building2 className="w-6 h-6" />,
  iconColor: 'text-violet-600 dark:text-violet-400',
  iconBg: 'bg-violet-100 dark:bg-violet-900/30',
  badgeColor: 'bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-300',
};

const Config: React.FC = () => {
  const { isOwner } = useAuth();
  const [activeSection, setActiveSection] = useState<Section | null>(null);

  const SECTIONS = isOwner() ? [...BASE_SECTIONS, OWNER_SECTION] : BASE_SECTIONS;

  const current = SECTIONS.find(s => s.id === activeSection) ?? null;

  return (
    <div className="w-full h-full p-6">
      <div className="overflow-hidden border rounded-xl bg-card border-border h-full flex flex-col">

        {/* Header */}
        <div className="px-6 py-5 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            {activeSection && (
              <button
                onClick={() => setActiveSection(null)}
                className="flex items-center justify-center w-9 h-9 rounded-xl border border-border hover:bg-muted transition-colors flex-shrink-0"
                aria-label="Volver"
              >
                <ChevronLeft className="w-5 h-5 text-muted-foreground" />
              </button>
            )}
            <div
              className={`flex items-center justify-center w-11 h-11 rounded-xl flex-shrink-0 ${
                current ? current.iconBg : 'bg-indigo-100 dark:bg-indigo-900/30'
              }`}
            >
              {current ? (
                <span className={current.iconColor}>{current.icon}</span>
              ) : (
                <Settings className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              )}
            </div>
            <div>
              <h1 className="text-xl font-semibold text-foreground">
                {current ? current.title : 'Configuración del Sistema'}
              </h1>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {current
                  ? current.subtitle
                  : 'Seleccioná una sección para configurar los parámetros del sistema'}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        {!activeSection ? (
          /* ── Landing: grilla de tarjetas ── */
          <div className="flex-1 overflow-y-auto scrollbar-custom p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {SECTIONS.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className="group text-left p-5 border border-border rounded-xl bg-card hover:shadow-md hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
                >
                  {/* Icon */}
                  <div
                    className={`w-12 h-12 flex items-center justify-center rounded-xl mb-4 ${section.iconBg} transition-transform duration-200 group-hover:scale-105`}
                  >
                    <span className={section.iconColor}>{section.icon}</span>
                  </div>

                  {/* Title + badge */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-sm font-semibold text-foreground leading-tight">
                      {section.title}
                    </h3>
                  </div>

                  {/* Subtitle */}
                  <p className="text-xs font-medium text-muted-foreground mb-2">
                    {section.subtitle}
                  </p>

                  {/* Description */}
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {section.description}
                  </p>

                  {/* CTA */}
                  <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
                    <span className="text-xs font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                      Abrir configuración
                    </span>
                    <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors ml-auto" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          /* ── Section content ── */
          <div className="flex-1 overflow-y-auto scrollbar-custom">
            {activeSection === 'workItem' && <WorkItemConfig />}
            {activeSection === 'generalWorkItem' && <GeneralWorkCategoryConfig />}
            {activeSection === 'projectItem' && <ProjectItemConfig />}
            {activeSection === 'cacItem' && <CACTab />}
            {activeSection === 'companyProfile' && <CompanyProfileTab />}
          </div>
        )}

      </div>
    </div>
  );
};

export default Config;
